import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, FileText, Download } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const size = (b) => (!b ? "" : b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

/** Every file in the student's SharePoint folder, read through Campus Connections so coordinators
 *  don't need SharePoint site permissions. */
export const StudentFiles = ({ prId, open, onClose }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !prId) { setData(null); setError(""); return; }
    api.get(`/placement-requests/${prId}/paperwork-files`)
      .then(({ data }) => setData(data))
      .catch((e) => setError(formatApiError(e.response?.data?.detail)));
  }, [open, prId]);

  return (
    <Dialog open={!!open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="student-files-dialog">
        <DialogHeader><DialogTitle>Student files{data?.student_name ? ` — ${data.student_name}` : ""}</DialogTitle></DialogHeader>
        {error ? <p className="text-sm text-red-600" data-testid="student-files-error">{error}</p>
          : !data ? <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-cc-blue" /></div>
            : data.files.length === 0 ? <p className="text-sm text-zinc-500 py-6 text-center">Nothing has been uploaded for this student yet.</p>
              : (
                <div className="divide-y max-h-[60vh] overflow-y-auto cc-scroll">
                  {data.files.map((f) => (
                    <a key={f.id} href={`${BACKEND}/api/paperwork-files/${f.id}`} target="_blank" rel="noreferrer"
                      className="flex items-center justify-between gap-3 py-2.5 group" data-testid={`student-file-${f.id}`}>
                      <span className="flex items-center gap-2 min-w-0">
                        <FileText className="w-4 h-4 text-cc-blue shrink-0" />
                        <span className="text-sm text-zinc-800 truncate group-hover:underline">{f.name}</span>
                      </span>
                      <span className="text-xs text-zinc-400 shrink-0 flex items-center gap-2">
                        {size(f.size)}{f.modified ? ` · ${new Date(f.modified).toLocaleDateString()}` : ""}
                        <Download className="w-3.5 h-3.5" />
                      </span>
                    </a>
                  ))}
                </div>
              )}
        <p className="text-[11px] text-zinc-400">Files live in the company Microsoft 365 folder — Campus Connections reads them for you, so no SharePoint access is needed.</p>
      </DialogContent>
    </Dialog>
  );
};
